/* tslint:disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpRequest, HttpResponse, HttpHeaders } from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { WebConfiguration as __Configuration } from '../web-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';

import { ClientList } from '../models/client-list';
import { ClientSearchOptions } from '../models/client-search-options';
import { Client } from '../models/client';
import { Station } from '../models/station';
import { MainClientContract } from '../models/main-client-contract';
import { ClientContractCreate } from '../models/client-contract-create';
import { ClientContract } from '../models/client-contract';
import { ClientContractUpdate } from '../models/client-contract-update';

/**
 * All about the clients
 */
@Injectable({
  providedIn: 'root',
})
class ClientsService extends __BaseService {
  static readonly searchClientsPath = '/clients/search';
  static readonly getClientPath = '/clients/{uid}';
  static readonly getClientStationsPath = '/clients/{uid}/stations';
  static readonly getClientContractsPath = '/clients/{uid}/contracts';
  static readonly createClientContractPath = '/clients/{uid}/contracts';
  static readonly retrieveClientContractPath = '/clients/{uid}/contracts/{clientContractUid}';
  static readonly updateClientContractPath = '/clients/{uid}/contracts/{clientContractUid}';
  static readonly deleteClientcontractPath = '/clients/{uid}/contracts/{clientContractUid}';

  constructor(
    config: __Configuration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Search clients, with pagination
   *
   * Search clients, with pagination
   * @param params The `ClientsService.SearchClientsParams` containing the following parameters:
   *
   * - `sortFieldName`: Indicates the field to use for sorting the data:
   *     * NAME : the client's name
   *     * GROUP_CLIENT : the 'group client' of the client
   *
   * - `sortDirection`: Sort direction : ASC, DESC
   *
   * - `search`: The client search options object
   *
   * - `offset`: The list items offset
   *
   * - `limit`: The list items limit
   *
   * @return Success or Partial Content
   */
  searchClientsResponse(params: ClientsService.SearchClientsParams): __Observable<__StrictHttpResponse<ClientList | ClientList>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (params.sortFieldName != null) __params = __params.set('sortFieldName', params.sortFieldName.toString());
    if (params.sortDirection != null) __params = __params.set('sortDirection', params.sortDirection.toString());
    __body = params.search;
    if (params.offset != null) __params = __params.set('offset', params.offset.toString());
    if (params.limit != null) __params = __params.set('limit', params.limit.toString());
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/clients/search`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<ClientList | ClientList>;
      })
    );
  }
  /**
   * Search clients, with pagination
   *
   * Search clients, with pagination
   * @param params The `ClientsService.SearchClientsParams` containing the following parameters:
   *
   * - `sortFieldName`: Indicates the field to use for sorting the data:
   *     * NAME : the client's name
   *     * GROUP_CLIENT : the 'group client' of the client
   *
   * - `sortDirection`: Sort direction : ASC, DESC
   *
   * - `search`: The client search options object
   *
   * - `offset`: The list items offset
   *
   * - `limit`: The list items limit
   *
   * @return Success or Partial Content
   */
  searchClients(params: ClientsService.SearchClientsParams): __Observable<ClientList | ClientList> {
    return this.searchClientsResponse(params).pipe(
      __map(_r => _r.body as ClientList | ClientList)
    );
  }

  /**
   * Get a client
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getClientResponse(uid: string): __Observable<__StrictHttpResponse<Client>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/clients/${encodeURIComponent(String(uid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Client>;
      })
    );
  }
  /**
   * Get a client
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getClient(uid: string): __Observable<Client> {
    return this.getClientResponse(uid).pipe(
      __map(_r => _r.body as Client)
    );
  }

  /**
   * Get a client's stations
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getClientStationsResponse(uid: string): __Observable<__StrictHttpResponse<Array<Station>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/clients/${encodeURIComponent(String(uid))}/stations`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<Station>>;
      })
    );
  }
  /**
   * Get a client's stations
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getClientStations(uid: string): __Observable<Array<Station>> {
    return this.getClientStationsResponse(uid).pipe(
      __map(_r => _r.body as Array<Station>)
    );
  }

  /**
   * Get a client's contracts
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getClientContractsResponse(uid: string): __Observable<__StrictHttpResponse<Array<MainClientContract>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/clients/${encodeURIComponent(String(uid))}/contracts`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<MainClientContract>>;
      })
    );
  }
  /**
   * Get a client's contracts
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  getClientContracts(uid: string): __Observable<Array<MainClientContract>> {
    return this.getClientContractsResponse(uid).pipe(
      __map(_r => _r.body as Array<MainClientContract>)
    );
  }

  /**
   * Create a Client Contract
   * @param params The `ClientsService.CreateClientContractParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `clientContractCreate`: The client contract create object
   */
  createClientContractResponse(params: ClientsService.CreateClientContractParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    __body = params.clientContractCreate;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/clients/${encodeURIComponent(String(params.uid))}/contracts`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Create a Client Contract
   * @param params The `ClientsService.CreateClientContractParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `clientContractCreate`: The client contract create object
   */
  createClientContract(params: ClientsService.CreateClientContractParams): __Observable<null> {
    return this.createClientContractResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Retrieve a Client Contract
   * @param params The `ClientsService.RetrieveClientContractParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `clientContractUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  retrieveClientContractResponse(params: ClientsService.RetrieveClientContractParams): __Observable<__StrictHttpResponse<ClientContract>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/clients/${encodeURIComponent(String(params.uid))}/contracts/${encodeURIComponent(String(params.clientContractUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<ClientContract>;
      })
    );
  }
  /**
   * Retrieve a Client Contract
   * @param params The `ClientsService.RetrieveClientContractParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `clientContractUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  retrieveClientContract(params: ClientsService.RetrieveClientContractParams): __Observable<ClientContract> {
    return this.retrieveClientContractResponse(params).pipe(
      __map(_r => _r.body as ClientContract)
    );
  }

  /**
   * Update a Client Contract
   * @param params The `ClientsService.UpdateClientContractParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `clientContractUpdate`: The client contract update object
   *
   * - `clientContractUid`: The object ID. Must be a valid ID.
   */
  updateClientContractResponse(params: ClientsService.UpdateClientContractParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    __body = params.clientContractUpdate;

    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl + `/clients/${encodeURIComponent(String(params.uid))}/contracts/${encodeURIComponent(String(params.clientContractUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Update a Client Contract
   * @param params The `ClientsService.UpdateClientContractParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `clientContractUpdate`: The client contract update object
   *
   * - `clientContractUid`: The object ID. Must be a valid ID.
   */
  updateClientContract(params: ClientsService.UpdateClientContractParams): __Observable<null> {
    return this.updateClientContractResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Delete a Client Contract
   * @param params The `ClientsService.DeleteClientcontractParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `clientContractUid`: The object ID. Must be a valid ID.
   */
  deleteClientcontractResponse(params: ClientsService.DeleteClientcontractParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl + `/clients/${encodeURIComponent(String(params.uid))}/contracts/${encodeURIComponent(String(params.clientContractUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Delete a Client Contract
   * @param params The `ClientsService.DeleteClientcontractParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `clientContractUid`: The object ID. Must be a valid ID.
   */
  deleteClientcontract(params: ClientsService.DeleteClientcontractParams): __Observable<null> {
    return this.deleteClientcontractResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }
}

namespace ClientsService {

  /**
   * Parameters for searchClients
   */
  export interface SearchClientsParams {

    /**
     * Indicates the field to use for sorting the data:
     *   * NAME : the client's name
     *   * GROUP_CLIENT : the 'group client' of the client
     */
    sortFieldName?: 'NAME' | 'GROUP_CLIENT';

    /**
     * Sort direction : ASC, DESC
     */
    sortDirection?: 'ASC' | 'DESC';

    /**
     * The client search options object
     */
    search?: ClientSearchOptions;

    /**
     * The list items offset
     */
    offset?: number;

    /**
     * The list items limit
     */
    limit?: number;
  }

  /**
   * Parameters for createClientContract
   */
  export interface CreateClientContractParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The client contract create object
     */
    clientContractCreate: ClientContractCreate;
  }

  /**
   * Parameters for retrieveClientContract
   */
  export interface RetrieveClientContractParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    clientContractUid: string;
  }

  /**
   * Parameters for updateClientContract
   */
  export interface UpdateClientContractParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The client contract update object
     */
    clientContractUpdate: ClientContractUpdate;

    /**
     * The object ID. Must be a valid ID.
     */
    clientContractUid: string;
  }

  /**
   * Parameters for deleteClientcontract
   */
  export interface DeleteClientcontractParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    clientContractUid: string;
  }
}

export { ClientsService }
